import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, useTheme, useMediaQuery, Typography } from "@mui/material";
import { motion } from "framer-motion";

const DESIGN_REFERENCE_SIZE = 1000;
const ACTUAL_MAX_CONTENT_WIDTH = 1400;
const WIDE_SCREEN_QUERY = "@media (min-width: 1540px)";

const BASE_SIZES = {
  icon: 320,
  commentWidth: 320,
  // SP表示時、各サービスのcommentWidthSp(110〜150)がだいたいこの値に近いため、
  // フォントサイズ計算の共通基準として使う（PC用の320を流用すると文字が大きすぎて折り返しが増える）。
  commentWidthSp: 120,
  circleBg: 700,
};

const SERVICES_DATA = [
  {
    img: "service/service1.png",
    title: "新規顧客獲得支援",
    items: [
      "動画制作・キャンペーン企画",
      "展示会・各種ツール制作",
      "LP制作・web広告配信",
      "ロゴ・CI・キャラクター開発",
    ],
    angle: -150,
    distance: 350,
    offset: { x: -75, y: 10 },
    commentOffset: { x: -280, y: 262 },
    commentOffsetSp: { x: -300.96, y: 250 },
    commentWidthSp: 120,
    // SP表示でこのサービスだけ項目数が多く窮屈になりやすいため、
    // 共通のSP_COMMENT_WIDTH_RATIOより個別に広げる。
    commentWidthRatioSp: 0.32,
    // 画面幅が非常に狭い(VERY_NARROW_THRESHOLD未満)時だけ使う配置。
    // 継続利用促進支援のアイコンと重なってしまうため、自分のアイコンの
    // 真上に表示する位置に切り替える。
    commentOffsetSpNarrow: { x: -300, y: -410 },
  },
  {
    img: "service/service2.png",
    title: "接触頻度向上支援",
    items: ["YouTubeチャンネル運用", "SNS運用・ライブ配信", "イベント企画・運営"],
    angle: 0,
    distance: 350,
    offset: { x: 25, y: -10 },
    commentOffset: { x: 250, y: -120 },
    commentOffsetSp: { x: -70, y: -240 },
    commentWidthSp: 110,
  },
  {
    img: "service/service3.png",
    title: "継続利用促進支援",
    items: [
      "e-ラーニングコンテンツ制作",
      "マニュアル・サポートwebサイト設計",
      "資料作成・株主総会対応",
    ],
    angle: 120,
    distance: 350,
    offset: { x: 0, y: 20 },
    commentOffset: { x: 230, y: 260 },
    commentOffsetSp: { x: 300, y: 180 },
    commentWidth: 400, // ★ 修正点1: PC表示用の個別幅を指定
    commentWidthSp: 170,
  },
];

const serviceItemVariants = {
  // スクロール到達前の非表示状態。display:noneではなくopacityで隠すことで、
  // テキストはDOM上に常時存在させ、クローラーやAIが読み取れるようにする。
  initial: { opacity: 0, y: 120, scale: 0.9 },
  animate: (index) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: "easeOut",
      delay: index * 0.3,
    },
  }),
};

const iconHoverEffect = {
  scale: 1.05,
  y: -5,
  rotate: [0, -3, 3, -2, 2, -1, 1, 0],
  transition: {
    scale: { duration: 0.25, ease: "easeOut" },
    y: { duration: 0.25, ease: "easeOut" },
    rotate: { duration: 0.4, ease: "linear" },
  },
};

const staticStyles = {
  sectionContainer: {
    height: "380vh",
    position: "relative",
    zIndex: 10,
  },
  stickyContainer: {
    position: "sticky",
    top: {
      xs: "-10vh",
      sm: "-0vh",
      md: "-0vh",
      lg: "-34vh",
    },
    minHeight: {
      xs: "max(640px, calc(100vw + 160px))",
      sm: "900px",
      md: "1100px",
      lg: "1270px",
    },
    height: {
      xs: "0vh",
      sm: "0vh",
      md: "0vh",
      lg: "130vh",
    },
    width: "100%",
    paddingTop: { xs: 8, sm: 0, md: 10, lg: 8 },
    backgroundColor: "white",
    borderTopLeftRadius: { xs: 80, sm: 120, md: 160 },
    borderTopRightRadius: { xs: 80, sm: 120, md: 160 },
    overflow: "hidden",
    marginTop: { xs: -20, md: -40 },
    [WIDE_SCREEN_QUERY]: { height: "auto" },
  },
  headerOuterContainer: {
    textAlign: "center",
    position: "relative",
  },
  headerBackgroundTitle: {
    position: "absolute",
    top: { xs: "-60px", sm: "-100px", md: "-160px" },
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: { xs: "64px", sm: "8rem", md: "11rem" },
    fontWeight: "bold",
    color: "rgba(230, 0, 18, 0.05)",
    zIndex: 0,
    userSelect: "none",
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  headerContentContainer: {
    textAlign: "center",
    position: "relative",
    zIndex: 1,
  },
  headerMainTitle: {
    color: "#E60012",
    display: "inline-block",
    marginBottom: 1,
    fontWeight: "bold",
    fontSize: { xs: "16px", sm: "2rem", md: "2.25rem" },
  },
  headerDivider: {
    height: 2,
    width: "6%",
    minWidth: "60px",
    backgroundColor: "#1D2D5C",
    margin: "0 auto",
  },
  contentAreaContainer: {
    position: "relative",
    width: { xs: "100%", sm: "95%", md: "90%", lg: "85%" },
    maxWidth: ACTUAL_MAX_CONTENT_WIDTH,
    aspectRatio: "1 / 1",
    margin: "0 auto",
    marginTop: { sm: "-160px", md: "-160px" },
  },
  serviceItemIconImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
};

const Service = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactSp = useMediaQuery("(max-width: 440px)");
  const isSmallSp = useMediaQuery("(max-width: 400px)");
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const dynamicStyles = {
    headerDescriptionText: {
      marginTop: 2,
      fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
      color: theme.palette.text.secondary,
      maxWidth: { xs: "340px", sm: "600px", md: "1000px" },
      margin: "0 auto",
      fontWeight: 500,
    },
  };

  const sectionScrollRef = useRef(null);
  const contentAreaRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(DESIGN_REFERENCE_SIZE);
  const [step, setStep] = useState(0);

  // 画面幅がこれ未満になったら「新規顧客獲得支援」を自分のアイコンの真上に
  // 表示する特別な配置に切り替える（継続利用促進支援のアイコンとの重なり対策）。
  const VERY_NARROW_THRESHOLD = 360;
  const isVeryNarrow = isXs && containerWidth < VERY_NARROW_THRESHOLD;

  useEffect(() => {
    const element = contentAreaRef.current;
    if (!element) return;

    setContainerWidth(element.getBoundingClientRect().width);

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(element);

    return () => observer.unobserve(element);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const section = sectionScrollRef.current;
      if (!section) return;

      const sectionTop = section.offsetTop;
      const scrollY = window.scrollY;
      const relativeY = scrollY - sectionTop;
      const screenHeight = window.innerHeight;

      if (relativeY < 0) return setStep(0);
      if (relativeY < screenHeight) return setStep(1);
      if (relativeY < screenHeight * 2) return setStep(2);
      return setStep(3);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const contentMetrics = useMemo(() => {
    let minX = Infinity;
    let maxX = -Infinity;

    SERVICES_DATA.forEach((serviceConfig) => {
      const angleRad = (serviceConfig.angle * Math.PI) / 180;
      const iconCenterXDesign =
        serviceConfig.distance * Math.cos(angleRad) + serviceConfig.offset.x;
      const iconLeftXDesign = iconCenterXDesign - BASE_SIZES.icon / 2;
      const iconRightXDesign = iconCenterXDesign + BASE_SIZES.icon / 2;

      const useSpLayout = isXs;
      const commentOffsetX_design = useSpLayout
        ? serviceConfig.commentOffsetSp.x
        : serviceConfig.commentOffset.x;
      // ★根本修正1: SP表示時はPC用の固定幅(320)ではなく、実際に使われる
      // commentWidthSpを使って全体の基準点・縮尺を計算する。
      // ここがPC用の値のままだと、SP表示の見た目とscale計算が食い違い、
      // 画面幅によって表示が崩れる原因になっていた。
      const commentWidthDesign = useSpLayout
        ? (serviceConfig.commentWidthSp ?? BASE_SIZES.commentWidthSp)
        : (serviceConfig.commentWidth ?? BASE_SIZES.commentWidth);

      const commentLeftXDesign = iconLeftXDesign + commentOffsetX_design;
      const commentRightXDesign = commentLeftXDesign + commentWidthDesign;

      minX = Math.min(minX, iconLeftXDesign, commentLeftXDesign);
      maxX = Math.max(maxX, iconRightXDesign, commentRightXDesign);
    });

    const effectiveWidth = maxX - minX;
    // スマホ幅では基準点を少し右下にずらすことで、
    // 図形全体の見た目上の位置を左上寄りに調整する。
    // ただし「新規顧客獲得支援」を真上に配置する特別モード(isVeryNarrow)では
    // 右側に余白ができるため、左上寄せの量を弱めて右側のスペースを活かす。
    const centerXOffset = isXs ? 0 : (maxX + minX) / 2;

    if (!isFinite(effectiveWidth) || effectiveWidth === 0) {
      return { effectiveWidth: DESIGN_REFERENCE_SIZE, centerXOffset: 0 };
    }

    return { effectiveWidth, centerXOffset };
  }, [isXs]);

  const overallScale = containerWidth / contentMetrics.effectiveWidth;
  const breakpointScaleFactor = isXs ? 0.85 : isSm ? 0.88 : 1;
  // contentMetrics（基準点・全体サイズ）と実際の描画が同じ値を参照するようになったため、
  // scaleに人為的な下限を設けなくても自己矛盾は起きない。下限を設けるとcontentMetricsの
  // 想定サイズと実際のscaleがズレて、かえってコンテナからはみ出す原因になる。
  const scale = overallScale * breakpointScaleFactor;

  const scaledSizes = useMemo(
    () => ({
      icon: BASE_SIZES.icon * scale,
      commentWidth: BASE_SIZES.commentWidth * scale,
      // ★根本修正2: SP用の共通基準幅（サービスごとの個別値に関わらず統一）。
      // 文字サイズの計算をこちらに揃えることで、ボックス幅がサービスごとに
      // 違っても文字サイズだけは統一される。
      commentWidthSp: BASE_SIZES.commentWidthSp * scale,
      circleBg: BASE_SIZES.circleBg * scale,
    }),
    [scale],
  );

  return (
    <Box id="service" ref={sectionScrollRef} sx={staticStyles.sectionContainer}>
      <Box sx={{
        ...staticStyles.stickyContainer,
        ...(isXs && {
          top: "min(0px, calc(100dvh - 100vw - 400px))",
          minHeight: 0,
          height: "auto",
          paddingTop: "64px",
        }),
      }}>
        <Box
          sx={{
            position: "absolute",
            top: { xs: "20%", sm: "25%", md: "30%", lg: "40%" },
            left: "50%",
            transform: {
              xs: "translate(-50%, -40%)",
              sm: "translate(-50%, -40%)",
              md: "translate(-50%, -40%)",
              lg: "translate(-50%, -45%)",
            },
            width: "100%",
            height: "auto",
            ...(isXs && {
              position: "relative",
              top: "auto",
              left: "auto",
              transform: "none",
            }),
            [WIDE_SCREEN_QUERY]: {
              position: "relative",
              top: "auto",
              left: "auto",
              transform: "none",
            },
          }}
        >
          <Box
            sx={{
              ...staticStyles.headerOuterContainer,
              mt: { xs: 25, sm: 38, md: 46 },
              ...(isXs && { mt: 0 }),
              [WIDE_SCREEN_QUERY]: { mt: 0 },
            }}
          >
            <Typography
              variant="h1"
              component="div"
              aria-hidden="true"
              sx={{
                ...staticStyles.headerBackgroundTitle,
                top: { xs: "-15%", sm: "-25%", md: "-30%" },
                fontFamily: "Roboto, sans-serif",
              }}
            >
              Service
            </Typography>
            <Box
              sx={{
                ...staticStyles.headerContentContainer,
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  ...staticStyles.headerMainTitle,
                  mt: { xs: 1.5, sm: 3, md: 5 },
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                サービス紹介
              </Typography>

              <Box
                sx={{
                  height: 2,
                  width: "3.5%",
                  minWidth: "60px",
                  backgroundColor: "#1D2D5C",
                  margin: "8px auto 24px",
                  mx: "auto",
                }}
              />

              <Typography
                variant="body1"
                sx={{
                  ...dynamicStyles.headerDescriptionText,
                  mt: { xs: 4, sm: 6, md: 8 },
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                企業の成長を支えるための多様なサービスを提供しています。
                動画制作からSNS運用まで、幅広く対応可能です。
              </Typography>
            </Box>
          </Box>

          <Box ref={contentAreaRef} sx={{
            ...staticStyles.contentAreaContainer,
            ...(isSmallSp && {
              marginTop: "40px",
              marginBottom: "40px",
            }),
            ...(isXs && !isCompactSp && {
              aspectRatio: "auto",
              height: "calc(100vw + 80px)",
            }),
          }}>
            <Box
              component="img"
              src="service/service-bg.png"
              alt="背景サークル"
              sx={{
                position: "absolute",
                width: scaledSizes.circleBg,
                height: scaledSizes.circleBg,
                top: isXs ? containerWidth / 2 : "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                objectFit: "contain",
                zIndex: 0,
              }}
            />

            {SERVICES_DATA.map((serviceConfig, index) => {
              // スクロール量に応じて見た目上は隠す/見せるだけにし、
              // DOM自体は常にマウントしておく（AIクローラー対策）。
              const isRevealed = step > index;

              const angleRad = (serviceConfig.angle * Math.PI) / 180;
              const iconCenterX_designOrigin =
                serviceConfig.distance * Math.cos(angleRad) +
                serviceConfig.offset.x;
              const iconCenterY_designOrigin =
                serviceConfig.distance * Math.sin(angleRad) +
                serviceConfig.offset.y;

              const iconCenterX_from_contentTrueCenter =
                iconCenterX_designOrigin - contentMetrics.centerXOffset;
              const iconCenterY_from_contentTrueCenter =
                iconCenterY_designOrigin - contentMetrics.centerXOffset;

              const finalIconCenterX =
                containerWidth / 2 + iconCenterX_from_contentTrueCenter * scale;
              const finalIconCenterY =
                containerWidth / 2 + iconCenterY_from_contentTrueCenter * scale;

              const finalIconX = finalIconCenterX - scaledSizes.icon / 2;
              const finalIconY = finalIconCenterY - scaledSizes.icon / 2;

              const useSpLayout = isXs;
              const useNarrowOverride =
                isVeryNarrow && serviceConfig.commentOffsetSpNarrow;
              const spOffset = useNarrowOverride
                ? serviceConfig.commentOffsetSpNarrow
                : serviceConfig.commentOffsetSp;
              const commentRelOffsetX_design = useSpLayout
                ? spOffset.x
                : serviceConfig.commentOffset.x;
              const commentRelOffsetY_design = useSpLayout
                ? spOffset.y
                : serviceConfig.commentOffset.y;

              const finalCommentRelX = commentRelOffsetX_design * scale;
              const finalCommentRelY = commentRelOffsetY_design * scale;

              // SPはアイコンと同じ縮小率(scale)に乗せると画面が狭いほど極端に
              // 幅が狭くなり縦長化してしまうため、ボックス幅だけは画面幅
              // (containerWidth)に対する割合で決める（横長のレイアウトを維持する）。
              // さらに、ボックスの絶対位置(アイコン位置+オフセット)からコンテナ右端までの
              // 残り幅を超えないようクランプし、位置によらず画面外にはみ出さないようにする。
              const SP_COMMENT_WIDTH_RATIO = 0.28;
              const SP_CONTAINER_MARGIN = 8;
              const boxAbsoluteLeft = finalIconX + finalCommentRelX;
              // 下限を高く設定すると、はみ出す位置でも無理やり広げてしまい
              // クランプが機能しなくなるため、下限は最小限(60px)に留める。
              const maxWidthWithinContainer = Math.max(
                60,
                containerWidth - boxAbsoluteLeft - SP_CONTAINER_MARGIN,
              );
              const widthRatioSp =
                serviceConfig.commentWidthRatioSp ?? SP_COMMENT_WIDTH_RATIO;
              // 比率だけだと画面が広い時(599px付近)に大きくなりすぎるため、
              // 上限(px)も設けておく。
              const maxCommentWidthPx = serviceConfig.commentMaxWidthSp ?? 150;
              const finalCommentWidth = useSpLayout
                ? Math.min(
                    containerWidth * widthRatioSp,
                    maxWidthWithinContainer,
                    maxCommentWidthPx,
                  )
                : (serviceConfig.commentWidth ?? BASE_SIZES.commentWidth) *
                  scale;

              // フォントサイズ・余白は実際のボックス幅から計算する。
              // SPはfinalCommentWidthが全サービス共通(containerWidth基準)なので
              // そのまま使えば、サービスごとに文字サイズがバラつくこともない。
              const fontRefWidth = useSpLayout
                ? finalCommentWidth
                : scaledSizes.commentWidth;
              const titleFontSize = Math.max(10, fontRefWidth * 0.078);
              const itemFontSize = Math.max(8, fontRefWidth * 0.064);
              const boxPadding = Math.max(6, fontRefWidth * 0.06);

              return (
                <motion.div
                  key={index}
                  custom={index}
                  variants={serviceItemVariants}
                  initial="initial"
                  animate={isRevealed ? "animate" : "initial"}
                  style={{
                    position: "absolute",
                    left: finalIconX,
                    top: finalIconY,
                    width: scaledSizes.icon,
                    height: scaledSizes.icon,
                    zIndex: 1,
                    cursor: "default",
                  }}
                >
                  <motion.div
                    style={{ position: "relative", width: "100%", height: "100%" }}
                    whileHover={iconHoverEffect}
                  >
                    <Box
                      component="img"
                      src={serviceConfig.img}
                      alt=""
                      sx={staticStyles.serviceItemIconImage}
                    />
                    <Typography
                      component="h3"
                      sx={{
                        position: "absolute",
                        left: "48.3%",
                        top: "72%",
                        transform: "translate(-50%, -50%)",
                        width: "80%",
                        m: 0,
                        color: "#fff",
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: "bold",
                        fontSize: scaledSizes.icon * 0.078,
                        lineHeight: 1.2,
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {serviceConfig.title}
                    </Typography>
                  </motion.div>

                  <Box
                    component="section"
                    aria-label={serviceConfig.title}
                    sx={{
                      position: "absolute",
                      left: isXs
                        ? (index === 0 ? (isCompactSp ? -49.680303635511734 : 16 - finalIconX) : index === 1
                          ? -scaledSizes.icon * 0.082
                          : scaledSizes.icon * 0.72)
                        : finalCommentRelX,
                      top: isXs ? (index === 2 ? scaledSizes.icon * 0.8 : "auto") : finalCommentRelY,
                      bottom: isXs && index !== 2
                        ? scaledSizes.icon * 0.99
                        : "auto",
                      width: isXs ? containerWidth * 0.36 : finalCommentWidth,
                      boxSizing: isXs ? "border-box" : "content-box",
                      zIndex: 2,
                      backgroundColor: "#fff",
                      border: "1px solid #1D2D5C",
                      borderRadius: "8px",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                      padding: `${boxPadding}px`,
                    }}
                  >
                    <Typography
                      component="h3"
                      sx={{
                        color: "#1D2D5C",
                        fontWeight: "bold",
                        fontSize: titleFontSize,
                        fontFamily: "Roboto, sans-serif",
                        margin: 0,
                        mb: `${boxPadding * 0.5}px`,
                        lineHeight: 1.3,
                      }}
                    >
                      {serviceConfig.title}
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        listStyle: "none",
                        margin: 0,
                        padding: 0,
                      }}
                    >
                      {serviceConfig.items.map((item) => (
                        <Box
                          component="li"
                          key={item}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: `${boxPadding * 0.4}px`,
                            color: "#1D2D5C",
                            fontSize: itemFontSize,
                            fontFamily: "Roboto, sans-serif",
                            lineHeight: 1.4,
                            mt: `${boxPadding * 0.35}px`,
                            "&:first-of-type": { mt: 0 },
                          }}
                        >
                          <Box component="span" aria-hidden="true">
                            ○
                          </Box>
                          <Box component="span">{item}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Service;
